import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCorporateBondScreenerSidebarFilterContext,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import {
  useCorporateBondScreenerDictionariesContext,
} from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerDictionariesContext';
import {
  getActiveSegments,
  getSegmentSubindustryIds,
} from '@/creditflowresearch/application/CorporateBondScreener/segmentIndustryMapping';
import normalizeGroupSelection
  from '@/creditflowresearch/application/BondScreener/normalizeGroupSelection';
import buildIndustryTree, { type IIndustryNode, type IIndustryNodeWithCounts } from './buildIndustryTree';

const useIndustriesSubindustriesSection = () => {
  const { filters, getDraft, setLocalFilters, subscribe } = useCorporateBondScreenerSidebarFilterContext();
  const { industries: industriesAccessor } = useCorporateBondScreenerDictionariesContext();
  const industries = industriesAccessor.getAll();

  const tree: IIndustryNode[] = useMemo(
    () => buildIndustryTree(industries, filters?.industries ?? []),
    [industries, filters?.industries],
  );

  const selectSelected = useCallback(() => getDraft().selectedSubindustryIds ?? [], [getDraft]);
  const [selected, setSelected] = useState<string[]>(selectSelected);

  useEffect(() => {
    const sync = (): void => setSelected(selectSelected());
    sync();
    return subscribe(sync);
  }, [selectSelected, subscribe]);

  const updateSelection = useCallback((next: string[]) => {
    const groups = industries.map((i) => ({ id: i.id, childIds: i.subindustries.map((sub) => sub.id) }));
    const {
      groupIds: includedIndustryIds,
      childIds: includedSubindustryIds,
    } = normalizeGroupSelection(next, groups);

    setLocalFilters((prev) => ({
      ...prev,
      includedIndustryIds,
      includedSubindustryIds,
      selectedSubindustryIds: next,
    }));
  }, [industries, setLocalFilters]);

  const segments = useMemo(() => {
    const allSubindustries = tree.flatMap((industry) => industry.children);
    return getActiveSegments(getSegmentSubindustryIds(allSubindustries), selected);
  }, [tree, selected]);

  const industryItems: IIndustryNodeWithCounts[] = useMemo(() => tree.map((industry) => {
    const enabledIds = industry.children.filter((sub) => !sub.disabled).map((sub) => sub.id);
    const allIds = industry.children.map((sub) => sub.id);
    return {
      ...industry,
      enabledIds,
      allIds,
      selectedCount: enabledIds.filter((id) => selected.includes(id)).length,
      totalSelectedCount: allIds.filter((id) => selected.includes(id)).length,
    };
  }), [tree, selected]);

  return { industryItems, selected, updateSelection, segments };
};

export default useIndustriesSubindustriesSection;
